import React, { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";

interface Station {
  _id: string;
  name: string;
  latLng: [number, number];
  displayName?: string;
}

interface Props {
  markers: Station[];
  onMarkerSelection: (index: string) => void;
}

const COLORS = {
  ocean: "#f0f7ff",
  land: "#e2e8f0",
  landStroke: "#cbd5e1",
  markerFill: "#0ea5e9",
  markerStroke: "#fff",
  markerPulse: "#0ea5e9",
  labelFill: "#334155",
  graticule: "#e2e8f0",
};

const VB_W = 960;
const VB_H = 500;

function Map({ markers, onMarkerSelection }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const initialized = useRef(false);
  const markersRef = useRef(markers);
  const onSelectRef = useRef(onMarkerSelection);

  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    text: string;
    x: number;
    y: number;
  }>({ visible: false, text: "", x: 0, y: 0 });

  // Keep refs in sync so D3 callbacks use latest values
  markersRef.current = markers;
  onSelectRef.current = onMarkerSelection;

  const showTooltip = useCallback((evt: MouseEvent, text: string) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    setTooltip({
      visible: true,
      text,
      x: evt.clientX - rect.left + 12,
      y: evt.clientY - rect.top - 8,
    });
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  useEffect(() => {
    if (!svgRef.current || initialized.current) return;
    initialized.current = true;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const projection = d3
      .geoNaturalEarth1()
      .fitSize([VB_W - 40, VB_H - 20], { type: "Sphere" } as any)
      .translate([VB_W / 2, VB_H / 2]);

    const path = d3.geoPath().projection(projection);

    const root = svg.append("g");

    // Ocean
    root
      .append("path")
      .datum({ type: "Sphere" } as any)
      .attr("d", path as any)
      .attr("fill", COLORS.ocean)
      .attr("stroke", COLORS.landStroke)
      .attr("stroke-width", 0.5);

    // Graticule
    root
      .append("path")
      .datum(d3.geoGraticule10())
      .attr("d", path as any)
      .attr("fill", "none")
      .attr("stroke", COLORS.graticule)
      .attr("stroke-width", 0.3);

    const landGroup = root.append("g").attr("class", "land");
    const markerGroup = root.append("g").attr("class", "markers");

    // Zoom
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 12])
      .translateExtent([
        [0, 0],
        [VB_W, VB_H],
      ])
      .on("zoom", (evt) => {
        root.attr("transform", evt.transform);
        const k = evt.transform.k;
        markerGroup.selectAll<SVGCircleElement, unknown>(".station-dot").attr("r", 5 / k);
        markerGroup.selectAll<SVGCircleElement, unknown>(".station-ring").attr("r", 9 / k);
        markerGroup
          .selectAll<SVGTextElement, unknown>(".station-label")
          .attr("font-size", `${10 / k}px`)
          .attr("dx", 8 / k);
        markerGroup
          .selectAll<SVGCircleElement, unknown>(".station-dot, .station-ring")
          .attr("stroke-width", 1.5 / k);
      });
    svg.call(zoom);

    d3.json<any>("/world.geojson").then((world) => {
      if (!world) return;
      landGroup
        .selectAll("path")
        .data(world.features)
        .enter()
        .append("path")
        .attr("d", (d: any) => path(d)!)
        .attr("fill", COLORS.land)
        .attr("stroke", COLORS.landStroke)
        .attr("stroke-width", 0.4);

      drawMarkers(
        markerGroup,
        projection,
        markersRef.current,
        onSelectRef,
        showTooltip,
        hideTooltip
      );
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redraw markers when data changes
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const markerGroup = svg.select<SVGGElement>("g.markers");
    if (markerGroup.empty()) return;

    const projection = d3
      .geoNaturalEarth1()
      .fitSize([VB_W - 40, VB_H - 20], { type: "Sphere" } as any)
      .translate([VB_W / 2, VB_H / 2]);

    drawMarkers(
      markerGroup,
      projection,
      markers,
      onSelectRef,
      showTooltip,
      hideTooltip
    );
  }, [markers, showTooltip, hideTooltip]);

  return (
    <div ref={wrapperRef} className="relative w-full h-full">
      {tooltip.visible && (
        <div
          className="absolute z-50 rounded bg-gray-800 px-2 py-1 text-xs text-white shadow pointer-events-none whitespace-nowrap"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.text}
        </div>
      )}
      <svg
        ref={svgRef}
        className="w-full h-full"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ background: COLORS.ocean }}
      />
    </div>
  );
}

function drawMarkers(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  projection: d3.GeoProjection,
  markers: Station[],
  onSelectRef: React.MutableRefObject<(index: string) => void>,
  showTooltip: (evt: MouseEvent, text: string) => void,
  hideTooltip: () => void
) {
  g.selectAll("*").remove();

  const defs = g.append("defs");
  const radialGrad = defs
    .append("radialGradient")
    .attr("id", "pulse-grad");
  radialGrad.append("stop").attr("offset", "0%").attr("stop-color", COLORS.markerPulse).attr("stop-opacity", 0.5);
  radialGrad.append("stop").attr("offset", "100%").attr("stop-color", COLORS.markerPulse).attr("stop-opacity", 0);

  markers.forEach((station, i) => {
    if (!station.latLng) return;
    const coords: [number, number] = [station.latLng[1], station.latLng[0]];
    const pt = projection(coords);
    if (!pt) return;

    const mountpoint = station.name.toUpperCase();
    const select = () => onSelectRef.current(String(i));

    // Pulse ring
    g.append("circle")
      .attr("class", "station-ring")
      .attr("cx", pt[0])
      .attr("cy", pt[1])
      .attr("r", 9)
      .attr("fill", "url(#pulse-grad)")
      .attr("stroke", COLORS.markerPulse)
      .attr("stroke-width", 1.5)
      .attr("opacity", 0.6)
      .style("animation", "home-pulse 2s ease-out infinite");

    // Dot
    g.append("circle")
      .attr("class", "station-dot")
      .attr("cx", pt[0])
      .attr("cy", pt[1])
      .attr("r", 5)
      .attr("fill", COLORS.markerFill)
      .attr("stroke", COLORS.markerStroke)
      .attr("stroke-width", 1.5)
      .style("cursor", "pointer")
      .on("click", select)
      .on("mouseenter", (event: MouseEvent) => showTooltip(event, mountpoint))
      .on("mouseleave", hideTooltip);

    // Label (clickable)
    g.append("text")
      .attr("class", "station-label")
      .attr("x", pt[0])
      .attr("y", pt[1])
      .attr("dx", 8)
      .attr("dy", 3)
      .attr("font-size", "10px")
      .attr("font-weight", "600")
      .attr("fill", COLORS.labelFill)
      .style("cursor", "pointer")
      .text(station.displayName || station.name)
      .on("click", select)
      .on("mouseenter", (event: MouseEvent) => showTooltip(event, mountpoint))
      .on("mouseleave", hideTooltip);
  });
}

export default Map;
